import type { AtlasManifest } from '@atlas-manifest/types';
import { ObjectHeader } from '../components/ObjectHeader.js';
import { EmptyState } from '../components/EmptyState.js';
import { EntityChip } from '../components/EntityChip.js';
import { Chip } from '../components/Chip.js';
import { Icon } from '../components/Icon.js';
import { EnrichmentFields } from '../components/EnrichmentFields.js';
import { RelationshipsPanel } from '../components/RelationshipsPanel.js';
import { pathForEntity, pathsForEntity, cloudFunctionsForEntity } from '../state/selectors.js';
import { useAppStore } from '../state/store.js';
import { navigateToObject, navigate, navigateToObjectKind } from '../state/hashRouter.js';
import { FIRESTORE_PATH_ATTRIBUTE_DOCS } from '../state/attributeGlossary.js';
import { WorkflowTrail } from './WorkflowTrail.js';
import { PresenceContainer } from './PresenceContainer.js';
import { AnnotationsContainer } from './AnnotationsContainer.js';
import { AskAtlasToEditBox } from '../components/AskAtlasToEditBox.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';

export interface FirestorePathDetailViewProps {
  manifest: AtlasManifest;
  entityId: string;
}

export function FirestorePathDetailView({ manifest, entityId }: FirestorePathDetailViewProps): React.JSX.Element {
  const path = pathForEntity(manifest, entityId);
  if (!path) {
    return <EmptyState icon="help" title={`No Firestore path for entity ${entityId}.`} />;
  }

  const owningEntityPaths = pathsForEntity(manifest, path.entityId);
  const functions = cloudFunctionsForEntity(manifest, path.entityId);

  const { entityTrail } = useAppStore();

  // Firestore collection/document paths can't contain literal '/' characters —
  // interpolating the raw path (e.g. /users/{uid}/holdings/{holdingId}) into
  // PresenceContainer/AnnotationsContainer's Firestore calls produces an
  // invalid collection reference. Slashes are replaced with underscores to
  // form a valid identifier. Caveat: two distinct real paths could
  // theoretically collide after this transform if one contains a literal
  // underscore where another has a slash in the same position (e.g.
  // /users/holdings vs /users_holdings) — not a risk with this codebase's
  // current path conventions, but worth checking if that ever changes.
  const firestoreDocId = path.path.replace(/\//g, '_');

  return (
    <div className="detail-view">
      <ObjectHeader
        object={path}
        icon="storage"
        iconLabel="Firestore path"
        badges={[]}
        title={path.path}
        docRef={<>retention: {path.retention}</>}
        actions={
          <>
            <button className="btn btn-ghost" onClick={() => navigate({ kind: 'sandbox', seedObject: { kind: 'firestore-path', id: path.path } })}>
              <Icon name="edit" size="sm" />
              Edit in Sandbox
            </button>
            <button className="btn btn-ghost" onClick={() => navigate({ kind: 'ask', focus: path.path })}>
              <Icon name="auto_awesome" size="sm" />
              Ask Atlas
            </button>
          </>
        }
      />
      <dl className="sheet-hero-stats" aria-label="stats">
        <div>
          <dt>INDEXES</dt>
          <dd>{(path.indexes ?? []).length}</dd>
        </div>
        <div>
          <dt>RETENTION</dt>
          <dd>{path.retention}</dd>
        </div>
      </dl>
      <EnrichmentFields
        fields={[
          { key: 'owner', label: 'Owner', value: path.owner, glossary: FIRESTORE_PATH_ATTRIBUTE_DOCS['owner'] },
          { key: 'accessPatterns', label: 'Access Patterns', value: path.accessPatterns, glossary: FIRESTORE_PATH_ATTRIBUTE_DOCS['accessPatterns'] },
          { key: 'writeOwner', label: 'Write Owner', value: path.writeOwner, glossary: FIRESTORE_PATH_ATTRIBUTE_DOCS['writeOwner'] },
          { key: 'quotaNotes', label: 'Quota Notes', value: path.quotaNotes, glossary: FIRESTORE_PATH_ATTRIBUTE_DOCS['quotaNotes'] },
          { key: 'migrationNotes', label: 'Migration Notes', value: path.migrationNotes, glossary: FIRESTORE_PATH_ATTRIBUTE_DOCS['migrationNotes'] },
          { key: 'compliance', label: 'Compliance', value: path.compliance, glossary: FIRESTORE_PATH_ATTRIBUTE_DOCS['compliance'] },
        ]}
      />
      {entityTrail.length >= 2 && (
        <WorkflowTrail trail={entityTrail} onJump={navigateToObject} />
      )}
      <PresenceContainer entityId={firestoreDocId} />
      <AskAtlasToEditBox
        kind="firestore-path"
        selectorId={path.path}
        currentObject={path as unknown as Record<string, unknown>}
        manifest={manifest}
        onAsk={(opts) => streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds)}
      />
      <section style={{ marginBottom: 'var(--sp-lg)' }}>
        <div className="section-label">Owning entity</div>
        <EntityChip entityId={path.entityId} manifest={manifest} />
      </section>
      <section style={{ marginBottom: 'var(--sp-lg)' }}>
        <div className="section-label">Functions · {functions.length}</div>
        {owningEntityPaths.length > 1 && (
          <p style={{ color: 'var(--caution)' }}>
            The owning entity claims more than one path — this list is attributed at the
            entity level and may include functions that touch a different one of its paths.
          </p>
        )}
        {functions.length > 0 ? (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {functions.map((fn) => (
              <Chip key={fn.name} label={fn.name} onClick={() => navigate({ kind: 'cloud-function', id: fn.name })} />
            ))}
          </div>
        ) : (
          <p>No cloud functions reference the owning entity.</p>
        )}
      </section>
      {path.indexes && path.indexes.length > 0 && (
        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <div className="section-label">Indexes · {path.indexes.length}</div>
          <p>{path.indexes.join(', ')}</p>
        </section>
      )}
      {path.securityRuleNote && (
        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <div className="section-label">Security rule note</div>
          <p>{path.securityRuleNote}</p>
        </section>
      )}
      <RelationshipsPanel manifest={manifest} kind="firestore-path" id={path.path} onNavigate={navigateToObjectKind} />
      <AnnotationsContainer entityId={firestoreDocId} />
    </div>
  );
}
